const NOOP_LOGGER = { debug: () => {} };

/**
 * Shared GS1 → item field mapper.
 *
 * This is the single source of truth for turning a parsed GS1 tag into item fields
 * (`assetNo`, `serialNo`, and the `organization:manufacturer` / `item:model` / `item:type`
 * lookups), reused by:
 *   - sensor-data-collection auto-add (`autoAddIfApplicable.mapGs1ToItem`)
 *   - Serialized Each tagId POST/import (rules-engine, Paths 4/5)
 *
 * The field-selection + lookup-resolution ORCHESTRATION lives here so the two silos can never
 * drift. All persistence is INJECTED (`getOrAdd`, `newId`, `tenantId`, `meta`) so each silo keeps
 * its own find-or-create primitive: sensor-data-collection injects `persist.getOrAdd`; the
 * rules-engine injects an adapter over `actions.find` + `actions.assure`. Nothing here imports a
 * database driver — `newId()` and `tenantId` are supplied by the caller.
 *
 * @param {string} tagId - hex or EPC-URN tag value to parse.
 * @param {Object} item - the item record to populate (MUTATED and returned).
 * @param {Object} deps
 * @param {(filter:Object, record:Object, namespace:string, relation:string)=>Promise<Object>} deps.getOrAdd
 *        Find a record matching `filter`, else create `record`; returns the persisted record.
 * @param {()=>*} deps.newId - factory for a fresh record id (e.g. `() => new ObjectId()`).
 * @param {*} deps.tenantId - tenant id stamped on any newly created lookup records.
 * @param {Object} [deps.meta] - meta stamped on any newly created lookup records.
 * @param {Object} [deps.caches] - optional in-memory dedup caches (batch optimization). Each entry
 *        is `{ get: ()=>Promise<Object[]>, clear: ()=>Promise<void> }`. Keys: manufacturers, models,
 *        itemTypes. Omit (rules-engine single-record path) to always go through `getOrAdd`.
 * @param {(target:('assetNo'|'manufacturer'|'model'|'type'|'serialNo'))=>boolean} [deps.shouldDerive]
 *        Gate per target so callers can honor non-overwrite semantics WITHOUT minting lookup records
 *        for client-supplied fields. Defaults to always-derive (sensor-data-collection auto-add,
 *        which builds the item from scratch).
 * @param {Object} [deps.logger] - logger with a `debug` method; defaults to a no-op logger (NOOP_LOGGER).
 * @returns {Promise<Object>} the mutated `item`.
 */
async function mapToItem(tagId, item, deps) {
    const { getOrAdd, newId, tenantId, meta, caches = {}, shouldDerive = () => true, logger = NOOP_LOGGER } = deps;

    // Parse via the module's own exported `parse` (resolved at call time) so unit tests that spy on
    // `gs1.parse` are honored and so we avoid a load-time circular require with ./index.
    const { parse } = require('./index');
    const gs1TdsValue = parse(tagId);
    if (!gs1TdsValue) {
        throw new Error(`Unable to parse '${tagId}' as a GS1 tag`);
    }

    // ---- assetNo: title first, then the type-specific identifier, finally the raw tag ----
    let title;
    if (gs1TdsValue.toTitle) {
        // SGTIN/SGLN/SSCC/GRAI/GDTI/CPI/SGCN expose a human-readable AI title.
        try {
            title = gs1TdsValue.toTitle();
        } catch (error) {
            logger.debug?.(`Unable to generate GS1 title for '${tagId}'. Falling back to parsed fields.`, error);
        }
    }
    if (shouldDerive('assetNo')) {
        if (title) {
            item.assetNo = title;
        } else if (gs1TdsValue.serial) {
            item.assetNo = gs1TdsValue.serial.toString();
        } else if (gs1TdsValue.serviceReference) {
            // GSRN when the AI title is not available
            item.assetNo = gs1TdsValue.serviceReference.toString();
        } else if (gs1TdsValue.assetReference) {
            // GIAI
            item.assetNo = gs1TdsValue.assetReference.toString();
        } else if (gs1TdsValue.location) {
            // SGLN
            item.assetNo = gs1TdsValue.location.toString();
        } else {
            // else just leave the tagId as the assetNo
            item.assetNo = tagId;
        }
    }

    // ---- organization:manufacturer (company prefix) ----
    if (gs1TdsValue.company && shouldDerive('manufacturer')) {
        const companyPrefix = gs1TdsValue.company.toString();
        const existing = caches.manufacturers ? await caches.manufacturers.get() : [];
        let dbRecord = existing.find(m => m.companyPrefix === companyPrefix || m.title === companyPrefix);
        if (!dbRecord) {
            dbRecord = { _id: newId(), title: companyPrefix, companyPrefix, tenantId, meta };
            dbRecord = await getOrAdd(
                { $or: [{ title: companyPrefix }, { companyPrefix }] },
                dbRecord,
                'organization',
                'manufacturer'
            );
            if (caches.manufacturers) await caches.manufacturers.clear();
        }
        item['organization:manufacturer'] = {
            _id: dbRecord._id,
            title: dbRecord.title,
            companyPrefix: dbRecord.companyPrefix
        };
    }

    // ---- item:model (SGTIN / CPI / SGCN product, component, or coupon reference) ----
    if (
        (gs1TdsValue.productNr || gs1TdsValue.componentPartReference || gs1TdsValue.couponReference) &&
        shouldDerive('model')
    ) {
        const {
            gtin: _gtin,
            productNr: _productNr,
            componentPartReference: _componentPartReference,
            couponReference: _couponReference,
            filter: _filter
        } = gs1TdsValue;
        const gtin = _gtin?.toString();
        const productNr = (_productNr || _componentPartReference || _couponReference)?.toString();
        const filter = _filter?.toString();
        const modelTitle = gtin || productNr;
        const existing = caches.models ? await caches.models.get() : [];
        let dbRecord = existing.find(m => m.productNr === productNr || m.title === modelTitle);
        if (!dbRecord) {
            dbRecord = { _id: newId(), title: modelTitle, productNr, filter, tenantId, meta };
            dbRecord = await getOrAdd({ $or: [{ title: modelTitle }, { productNr }] }, dbRecord, 'item', 'model');
            if (caches.models) await caches.models.clear();
        }
        // title = gtin or productNr
        item['item:model'] = { _id: dbRecord._id, title: dbRecord.title, productNr: dbRecord.productNr };
    }

    // ---- item:type (GRAI asset type / GDTI document type) ----
    if ((gs1TdsValue.assetType || gs1TdsValue.documentType) && shouldDerive('type')) {
        const { assetType: _assetType, documentType: _documentType } = gs1TdsValue;
        const assetType = (_assetType || _documentType)?.toString();
        const existing = caches.itemTypes ? await caches.itemTypes.get() : [];
        let dbRecord = existing.find(m => m.title === assetType);
        if (!dbRecord) {
            dbRecord = { _id: newId(), title: assetType, tenantId, meta };
            dbRecord = await getOrAdd({ title: assetType }, dbRecord, 'item', 'type');
            if (caches.itemTypes) await caches.itemTypes.clear();
        }
        item['item:type'] = { _id: dbRecord._id, title: dbRecord.title };
    }

    // ---- serialNo ----
    if ((gs1TdsValue.serial || gs1TdsValue.serviceReference) && shouldDerive('serialNo')) {
        item.serialNo = (gs1TdsValue.serial || gs1TdsValue.serviceReference).toString();
    }

    return item;
}

module.exports = mapToItem;
